import { FileText, Layers, Lightbulb, GraduationCap, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import Container from './Container';
import Reveal from '../../../components/Reveal';
import { useScrollProgress } from '../../../hooks/useScrollProgress';
import {
  NotesMockup,
  FlashcardsMockup,
  QuizMockup,
  ChatMockup,
  GroupMockup,
} from './mockups';
import andy from './../../../assets/images/askAndy.png';

const TOOLS = [
  {
    icon: FileText,
    name: 'Interactive Notes',
    desc: 'Test yourself with School Hack quizzes tailored to your exact notes. Instant feedback, instant learning.',
    Mockup: NotesMockup,
    to: '/features/interactive-notes',
  },
  {
    icon: Layers,
    name: 'Interactive Flashcards',
    desc: 'Auto-generated flashcards pull the key terms straight from your material so revision writes itself.',
    Mockup: FlashcardsMockup,
    to: '/features/interactive-flashcards',
  },
  {
    icon: Lightbulb,
    name: 'Interactive Quizzes',
    desc: 'Adaptive quizzes with instant explanations pinpoint exactly what to review before the exam.',
    Mockup: QuizMockup,
    to: '/features/interactive-quizzes',
  },
  {
    icon: GraduationCap,
    name: 'Interactive Teacher',
    desc: 'Ask Andy anything about your notes and get patient, grounded answers whenever you get stuck.',
    Mockup: ChatMockup,
    to: '/features/interactive-teacher',
  },
  {
    icon: Users,
    name: 'Collaborative Mode',
    desc: 'Study live with friends — share decks, compare progress, and keep each other accountable.',
    Mockup: GroupMockup,
    to: '/features/collaborative-mode',
  },
];

const N = TOOLS.length;

const Header = () => (
  <div className="text-center">
    <span className="eyebrow">Our Features</span>
    <h2 className="font-heading mt-5 text-[2rem] font-bold leading-[1.1] text-white sm:text-[2.6rem] lg:text-[3rem]">
      <span className="head-fade">Everything You Need to</span>
      <br />
      <span className="text-accent-grad">Ace Your Exams</span>
    </h2>
    <p className="mx-auto mt-4 max-w-xl text-[0.95rem] text-white/55">
      Five powerful AI tools that work together to transform how you learn — for
      free.
    </p>
  </div>
);

const Preview = ({ index }) => (
  <div className="relative mx-auto w-full max-w-[440px]">
    <div className="glow left-1/2 top-1/2 h-[340px] w-[340px] -translate-x-1/2 -translate-y-1/2 bg-accent/16" />
    <img
      src={andy}
      alt="Andy"
      className="absolute -right-4 -top-12 z-20 h-24 w-24 animate-float object-contain drop-shadow-[0_10px_30px_rgba(94,216,178,0.4)]"
    />
    <div className="card-dark relative z-10 h-[360px] overflow-hidden rounded-3xl">
      {TOOLS.map((t, i) => (
        <div
          key={t.name}
          className="absolute inset-0 transition-all duration-500 ease-out"
          style={{
            opacity: i === index ? 1 : 0,
            transform: i === index ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.98)',
            pointerEvents: i === index ? 'auto' : 'none',
          }}
        >
          <t.Mockup />
        </div>
      ))}
    </div>
    {/* dots */}
    <div className="mt-5 flex justify-center gap-2">
      {TOOLS.map((t, i) => (
        <span
          key={t.name}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i === index ? 'w-7 bg-accent' : 'w-1.5 bg-white/20'
          }`}
        />
      ))}
    </div>
  </div>
);

const ToolRow = ({ tool, isActive, fill, onClick }) => (
  <div
    className="flex w-full gap-4 text-left"
    style={{ opacity: isActive ? 1 : 0.45, transition: 'opacity 0.4s ease' }}
  >
    {/* progress rail */}
    <div className="relative mt-1 w-[3px] shrink-0 rounded-full bg-white/10">
      <span
        className="absolute left-0 top-0 w-full rounded-full bg-accent"
        style={{ height: `${fill * 100}%`, transition: 'height 0.1s linear' }}
      />
    </div>
    <div className="min-w-0">
      <button onClick={onClick} className="flex items-center gap-2.5 text-left">
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
            isActive ? 'bg-accent/15 text-accent' : 'bg-white/6 text-white/50'
          }`}
        >
          <tool.icon size={16} />
        </span>
        <span
          className={`font-heading text-[1.15rem] font-semibold transition-colors md:text-[1.35rem] ${
            isActive ? 'text-accent' : 'text-white'
          }`}
        >
          {tool.name}
        </span>
      </button>
      <div
        className="grid transition-all duration-500"
        style={{ gridTemplateRows: isActive ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden pl-[2.6rem] pt-2">
          <p className="text-[0.9rem] leading-relaxed text-white/60">{tool.desc}</p>
          {tool.to && (
            <Link
              to={tool.to}
              className="mt-3 inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-accent transition-all hover:gap-2.5"
            >
              Read more
              <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  </div>
);

const EverythingYouNeed = () => {
  const { ref, progress } = useScrollProgress();
  const scaled = progress * N;
  const index = Math.min(N - 1, Math.floor(scaled));

  return (
    <section id="everything" ref={ref} className="relative lg:h-[560vh]">
      {/* ============ Desktop: pinned scroll experience ============ */}
      <div className="sticky top-0 hidden h-screen items-center overflow-hidden lg:flex">
        <div className="glow left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative w-full">
          <Header />
          <div className="mt-12 grid grid-cols-[1fr_1.05fr] items-center gap-12">
            {/* left list */}
            <div className="flex flex-col gap-7">
              {TOOLS.map((t, i) => {
                const fill = Math.min(1, Math.max(0, scaled - i));
                return (
                  <ToolRow
                    key={t.name}
                    tool={t}
                    isActive={i === index}
                    fill={i < index ? 1 : i === index ? fill : 0}
                    onClick={() => {
                      const el = ref.current;
                      if (!el) return;
                      const distance = el.offsetHeight - window.innerHeight;
                      const target =
                        el.offsetTop + ((i + 0.5) / N) * distance;
                      window.scrollTo({ top: target, behavior: 'smooth' });
                    }}
                  />
                );
              })}
            </div>
            {/* right preview */}
            <Preview index={index} />
          </div>
        </Container>
      </div>

      {/* ============ Mobile: stacked fallback ============ */}
      <div className="lg:hidden">
        <Container className="py-16">
          <Reveal>
            <Header />
          </Reveal>
          <div className="mt-10 flex flex-col gap-10">
            {TOOLS.map((t, i) => (
              <Reveal key={t.name} delay={40}>
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15 text-accent">
                    <t.icon size={17} />
                  </span>
                  <span className="font-heading text-[1.3rem] font-semibold text-accent">
                    {t.name}
                  </span>
                </div>
                <p className="mt-2 text-[0.92rem] leading-relaxed text-white/60">
                  {t.desc}
                </p>
                {t.to && (
                  <Link
                    to={t.to}
                    className="mt-3 inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-accent"
                  >
                    Read more
                    <ArrowRight size={14} />
                  </Link>
                )}
                <div className="card-dark mt-5 h-[320px] overflow-hidden rounded-3xl">
                  <t.Mockup />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </div>
    </section>
  );
};

export default EverythingYouNeed;
